// src/pages/billing/InvoiceDetail.js

import { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, query, where, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useParams, useNavigate } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";

export default function InvoiceDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const { company } = useCompany();
  const [invoice,    setInvoice]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [processing, setProcessing] = useState(false);
  const [payments,   setPayments]   = useState([]);

  // Partial payment modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: "", mode: "Cash", date: new Date().toISOString().slice(0, 10), note: "",
  });
  const [payLoading, setPayLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (id) { fetchInvoice(); fetchPayments(); } }, [id]);

  // ── Lock service task after invoice created ──
  const lockServiceTask = async (taskId) => {
    if (!taskId) return;
    try {
      await updateDoc(doc(db, "service_tasks", taskId), {
        is_invoiced: true,
        invoice_id:  id,
        updated_at:  serverTimestamp(),
      });
    } catch (err) {
      console.warn("lockServiceTask failed (non-blocking):", err);
    }
  };

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, "invoices", id));
      if (!snap.exists()) { alert("Invoice not found"); navigate("/billing/invoices"); return; }
      const data = snap.data();
      if (company_id && data.company_id !== company_id) {
        alert("Unauthorized access"); navigate("/billing/invoices"); return;
      }
      setInvoice({ id: snap.id, ...data });
      // Lock service task if linked
      if (data.service_task_id) {
        lockServiceTask(data.service_task_id);
      }
      // 🆕 Invoice Created / Paid notifications hatao (on view)
      try {
        const { markNotificationReadByReference } =
          await import("../../services/notificationService");
        await markNotificationReadByReference(id, "invoice_created", company_id);
        await markNotificationReadByReference(id, "invoice_paid",    company_id);
      } catch (e) {
        console.warn("[InvoiceDetail] mark read failed (non-blocking):", e);
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      alert("Failed to load invoice");
    } finally { setLoading(false); }
  };

  const fetchPayments = async () => {
    try {
      const q = query(
        collection(db, "invoice_payments"),
        where("company_id", "==", company_id),
        where("invoice_id", "==", id),
        orderBy("created_at", "desc")
      );
      const snap = await getDocs(q);
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("fetchPayments error:", err);
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) { alert("Enter valid amount"); return; }
    const balance = Number(invoice.balance_amount ?? invoice.total_amount ?? 0);
    if (amount > balance) { alert(`Amount cannot exceed balance ₹${balance.toLocaleString()}`); return; }

    setPayLoading(true);
    try {
      const newPaid    = Number(invoice.paid_amount || 0) + amount;
      const newBalance = Number(invoice.total_amount || 0) - newPaid;
      const newStatus  = newBalance <= 0 ? "Paid" : "Partial";

      // Update invoice
      await updateDoc(doc(db, "invoices", id), {
        paid_amount:    newPaid,
        balance_amount: Math.max(0, newBalance),
        payment_status: newStatus,
        invoice_status: "Approved",
        updated_at:     serverTimestamp(),
      });

      // Save payment record
      await addDoc(collection(db, "invoice_payments"), {
        invoice_id:      id,
        invoice_no:      invoice.invoice_no,
        company_id,
        client_id:       invoice.client_id   || "",
        client_name:     invoice.client_name || "",
        amount,
        mode:            payForm.mode,
        payment_date:    payForm.date,
        note:            payForm.note.trim(),
        recorded_by:     user.uid || user.id || "",
        recorded_by_name: user.name || "",
        created_at:      serverTimestamp(),
      });

      // Financial transaction — project_id/project_name included so this
      // later payment (which may arrive weeks/months after the original
      // invoice, e.g. credit-based jobs) is ALSO correctly attributed to
      // the project in project_summary via onFinancialTransactionCreate.
      // This is exactly the gap that previously made later-arriving
      // payments invisible to any per-project costing view.
      await addDoc(collection(db, "financial_transactions"), {
        company_id,
        type:            "income",
        sub_type:        "invoice_payment",
        reference_id:    id,
        reference_no:    invoice.invoice_no,
        client_id:       invoice.client_id   || "",
        client_name:     invoice.client_name || "",
        project_id:      invoice.project_id   || "",
        project_name:    invoice.project_name || "",
        site_id:         invoice.site_id     || "",
        site_name:       invoice.site_name   || "",
        amount,
        paid_amount:     amount,
        balance_amount:  Math.max(0, newBalance),
        status:          newStatus.toLowerCase(),
        payment_mode:    payForm.mode,
        description:     `${newStatus === "Paid" ? "Full" : "Partial"} payment — ${invoice.invoice_no}`,
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
        created_at:      serverTimestamp(),
      });

      setShowPayModal(false);
      setPayForm({ amount: "", mode: "Cash", date: new Date().toISOString().slice(0, 10), note: "" });
      await fetchInvoice();
      await fetchPayments();

      // 🆕 Cash Collected notification hatao (accountant ne reconcile kiya)
      try {
        const { markNotificationReadByReference } =
          await import("../../services/notificationService");
        if (invoice.service_task_id) {
          await markNotificationReadByReference(
            invoice.service_task_id,
            "cash_collected",
            company_id
          );
        }
      } catch (e) {
        console.warn("[InvoiceDetail] cash_collected mark read failed (non-blocking):", e);
      }

    } catch (err) {
      console.error("Payment error:", err);
      alert("Failed to record payment: " + err.message);
    } finally { setPayLoading(false); }
  };
  const markAsPaid = async () => {
    if (!invoice) return;
    if (!window.confirm("Mark this invoice as fully paid?")) return;
    try {
      setProcessing(true);
      const remaining = Number(invoice.balance_amount ?? invoice.total_amount ?? 0);
      if (remaining > 0) {
        await addDoc(collection(db, "invoice_payments"), {
          invoice_id:      id,
          invoice_no:      invoice.invoice_no,
          company_id,
          client_id:       invoice.client_id   || "",
          client_name:     invoice.client_name || "",
          amount:          remaining,
          mode:            invoice.payment_mode || "Unknown",
          payment_date:    new Date().toISOString().slice(0, 10),
          note:            "Marked as fully paid",
          recorded_by:     user.uid || user.id || "",
          recorded_by_name: user.name || "",
          created_at:      serverTimestamp(),
        });
      }
      await updateDoc(doc(db, "invoices", invoice.id), {
        payment_status: "Paid",
        paid_amount:    invoice.total_amount,
        balance_amount: 0,
        invoice_status: "Approved",
        updated_at:     serverTimestamp(),
      });
      // Only write a financial_transactions entry for what's genuinely
      // being collected RIGHT NOW (the remaining balance) — never the
      // full invoice total. If some amount was already collected earlier
      // (e.g. cash collected at job-close via CreateInvoice.js's
      // "collected at creation" entry, or an earlier partial Record
      // Payment), writing the full total_amount here would double-count
      // that earlier amount all over again — the exact same bug pattern
      // already fixed in CreateInvoice.js. `remaining` is always the
      // correct amount: whatever hasn't been recorded as income yet.
      if (remaining > 0) {
        await addDoc(collection(db, "financial_transactions"), {
          company_id,
          type:            "income",
          sub_type:        "invoice_payment",
          reference_id:    invoice.id,
          reference_no:    invoice.invoice_no,
          client_id:       invoice.client_id   || "",
          client_name:     invoice.client_name || "",
          project_id:      invoice.project_id   || "",
          project_name:    invoice.project_name || "",
          site_id:         invoice.site_id     || "",
          site_name:       invoice.site_name   || "",
          amount:          remaining,
          paid_amount:     remaining,
          balance_amount:  0,
          status:          "paid",
          payment_mode:    invoice.payment_mode || "Unknown",
          description:     `Full payment — ${invoice.invoice_no}`,
          created_by:      user.uid || user.id || "",
          created_by_name: user.name || "",
          created_at:      serverTimestamp(),
        });
      }
      await fetchInvoice();
      await fetchPayments();

      // 🆕 Cash Collected + invoice_paid notification hatao
      try {
        const { markNotificationReadByReference } =
          await import("../../services/notificationService");
        if (invoice.service_task_id) {
          await markNotificationReadByReference(
            invoice.service_task_id,
            "cash_collected",
            company_id
          );
        }
        await markNotificationReadByReference(id, "invoice_paid", company_id);
      } catch (e) {
        console.warn("[InvoiceDetail] markAsPaid mark read failed (non-blocking):", e);
      }

    } catch (err) {
      console.error("Payment update error:", err);
      alert("Failed to update payment");
    } finally { setProcessing(false); }
  };

  const approveInvoice = async () => {
    try {
      setProcessing(true);
      await updateDoc(doc(db, "invoices", invoice.id), {
        invoice_status:   "Approved",
        approved_by:      user.uid || user.id || "",
        approved_by_name: user.name || "",
        updated_at:       serverTimestamp(),
      });
      await fetchInvoice();
    } catch (err) {
      alert("Failed to approve");
    } finally { setProcessing(false); }
  };

  const handleWhatsApp = () => {
    const dueDate = invoice.due_date ? new Date(invoice.due_date).toLocaleDateString("en-IN") : "-";
    const balance = Number(invoice.balance_amount ?? invoice.total_amount ?? 0);
    const message =
`Invoice: ${invoice.invoice_no}
Client: ${invoice.client_name}
Site: ${invoice.site_name || "-"}
Total: ₹${Number(invoice.total_amount || 0).toLocaleString("en-IN")}
Balance Due: ₹${balance.toLocaleString("en-IN")}
Due Date: ${dueDate}
Payment Mode: ${invoice.payment_mode || "-"}

Please make the payment at your earliest convenience.
— ${company?.company_name || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleDownloadPDF = () => {
    const original = document.title;
    document.title = `${invoice.invoice_no} — ${invoice.client_name}`;
    window.print();
    document.title = original;
  };

  const statusBadge = (s) => ({
    Paid:    "bg-green-100 text-green-700",
    Pending: "bg-yellow-100 text-yellow-700",
    Partial: "bg-blue-100 text-blue-700",
    Overdue: "bg-red-100 text-red-700",
    Draft:   "bg-gray-100 text-gray-700",
  }[s] || "bg-gray-100 text-gray-700");

  const fmtDate = (ts) => {
    if (!ts) return "-";
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    if (typeof ts === "string") return new Date(ts).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return "-";
  };

  if (loading) return <div className="p-6"><p>Loading invoice...</p></div>;
  if (!invoice) return <div className="p-6"><p>Invoice not found.</p></div>;

  const balance  = Number(invoice.balance_amount ?? invoice.total_amount ?? 0);
  const isPaid   = invoice.payment_status === "Paid";

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <style>{`
        @media print {
          @page { margin: 10mm; size: A4; }
          body * { visibility: hidden; }
          #invoice-print-area, #invoice-print-area * { visibility: visible; }
          #invoice-print-area {
            position: fixed; top: 0; left: 0; right: 0;
            width: 100%; max-width: 100%; margin: 0; padding: 12px;
          }
        }
      `}</style>

      {/* Actions header */}
      <div className="flex flex-wrap justify-between items-center gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/billing/invoices")} className="text-gray-500 hover:text-gray-800 text-sm">← Back</button>
          <h1 className="text-xl font-bold">{invoice.invoice_no}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => navigate(`/billing/create?id=${invoice.id}`)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">
            ✏ Edit
          </button>
          <button onClick={handleDownloadPDF} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold">
            🖨 Print / PDF
          </button>
          <button onClick={handleWhatsApp} className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600">
            💬 WhatsApp
          </button>
          {invoice.invoice_status !== "Approved" && (
            <button onClick={approveInvoice} disabled={processing}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              Approve
            </button>
          )}
          {!isPaid && (
            <>
              <button onClick={() => setShowPayModal(true)} disabled={processing}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                💰 Record Payment
              </button>
              <button onClick={markAsPaid} disabled={processing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                ✅ Mark as Paid
              </button>
            </>
          )}
        </div>
      </div>

      {/* Invoice card */}
      <div id="invoice-print-area" className="bg-white rounded-xl shadow p-8 print:shadow-none">

        {/* Header */}
        <div className="flex justify-between border-b pb-6 mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">{company?.company_name || user.company_name || "Company"}</h2>
            <p className="text-sm text-gray-500 mt-1">{company?.company_address || ""}</p>
            <div className="flex gap-4 mt-1 text-xs text-gray-400">
              {company?.gst_number && <span>GSTIN: {company.gst_number}</span>}
              {company?.pan_number && <span>PAN: {company.pan_number}</span>}
              {company?.company_phone && <span>Mobile: {company.company_phone}</span>}
            </div>
          </div>
          <div className="text-right">
            <h3 className="text-xl font-semibold uppercase tracking-wide">
              Tax Invoice
            </h3>
            <p className="font-medium mt-1">{invoice.invoice_no}</p>
            <p className="text-sm text-gray-500">
              Date: {invoice.invoice_date?.seconds ? new Date(invoice.invoice_date.seconds * 1000).toLocaleDateString() : "-"}
            </p>
            {invoice.due_date && <p className="text-sm text-gray-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>}
          </div>
        </div>

        {/* Client + Ship To + Status */}
        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-6 mb-8">
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Bill To:</h4>
            <p className="font-medium">{invoice.client_name}</p>
            {invoice.client_gstin && <p className="text-sm text-gray-500">GSTIN: {invoice.client_gstin}</p>}
            {invoice.place_of_supply && <p className="text-sm text-gray-500">Place of Supply: {invoice.place_of_supply}</p>}
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Ship To:</h4>
            <p className="font-medium">{invoice.client_name}</p>
            {invoice.project_name && <p className="text-sm text-gray-500">Project: {invoice.project_name}</p>}
            {invoice.site_name    && <p className="text-sm text-gray-500">Site: {invoice.site_name}</p>}
            {invoice.service_task_id && <p className="text-sm text-gray-500">Service Job: {invoice.service_task_id}</p>}
          </div>
          <div className="text-right space-y-1 whitespace-nowrap">
            <div>
              Payment Status:{" "}
              <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${statusBadge(invoice.payment_status)}`}>
                {invoice.payment_status}
              </span>
            </div>
            <div className="text-sm text-gray-500">Invoice Status: <span className="font-medium text-gray-700">{invoice.invoice_status}</span></div>
          </div>
        </div>

        {/* Items */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                {["#","Item","HSN/SAC","Qty","Unit","Rate ₹","Amount ₹"].map(h => (
                  <th key={h} className="p-3 text-left border border-gray-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoice.line_items?.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-3 border border-gray-200 text-gray-500">{i + 1}</td>
                  <td className="p-3 border border-gray-200">
                    <div className="font-medium">{item.item_name}</div>
                    {item.description && <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>}
                  </td>
                  <td className="p-3 border border-gray-200 text-gray-500">{item.hsn_code || "-"}</td>
                  <td className="p-3 border border-gray-200 text-center">{item.qty}</td>
                  <td className="p-3 border border-gray-200 text-center">{item.unit}</td>
                  <td className="p-3 border border-gray-200 text-right">₹{Number(item.rate).toLocaleString()}</td>
                  <td className="p-3 border border-gray-200 text-right font-medium">₹{Number(item.taxable_amount ?? item.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* HSN-WISE SUMMARY — groups every line item by HSN code, showing
            taxable value + CGST + SGST per HSN, exactly matching the format
            GST return-filing needs. Purely derived from line_items — no
            separate storage, so it can never drift from the line-item detail. */}
        {invoice.line_items?.some(li => li.hsn_code) && (
          <div className="mt-8">
            <h4 className="font-semibold text-gray-700 mb-2 text-sm">HSN/SAC Summary</h4>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left border border-gray-200">HSN/SAC</th>
                  <th className="p-2 text-right border border-gray-200">Taxable Value ₹</th>
                  <th className="p-2 text-right border border-gray-200">CGST ₹</th>
                  <th className="p-2 text-right border border-gray-200">SGST ₹</th>
                  <th className="p-2 text-right border border-gray-200">Total Tax ₹</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(
                  (invoice.line_items || []).reduce((acc, li) => {
                    const key = li.hsn_code || "—";
                    if (!acc[key]) acc[key] = { hsn: key, taxable: 0, cgst: 0, sgst: 0 };
                    acc[key].taxable += Number(li.taxable_amount || 0);
                    acc[key].cgst    += Number(li.cgst_amount || 0);
                    acc[key].sgst    += Number(li.sgst_amount || 0);
                    return acc;
                  }, {})
                ).map(row => (
                  <tr key={row.hsn}>
                    <td className="p-2 border border-gray-200">{row.hsn}</td>
                    <td className="p-2 border border-gray-200 text-right">₹{row.taxable.toLocaleString()}</td>
                    <td className="p-2 border border-gray-200 text-right">₹{row.cgst.toFixed(2)}</td>
                    <td className="p-2 border border-gray-200 text-right">₹{row.sgst.toFixed(2)}</td>
                    <td className="p-2 border border-gray-200 text-right font-medium">₹{(row.cgst + row.sgst).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-gray-50">
                  <td className="p-2 border border-gray-200">Total</td>
                  <td className="p-2 border border-gray-200 text-right">₹{Number(invoice.subtotal || 0).toLocaleString()}</td>
                  <td className="p-2 border border-gray-200 text-right">₹{Number(invoice.total_cgst || 0).toFixed(2)}</td>
                  <td className="p-2 border border-gray-200 text-right">₹{Number(invoice.total_sgst || 0).toFixed(2)}</td>
                  <td className="p-2 border border-gray-200 text-right">₹{Number(invoice.tax_amount || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-full md:w-72 space-y-2 text-sm">
            <Row label="Subtotal (Taxable Value)" value={`₹${Number(invoice.subtotal || 0).toLocaleString()}`} />
            <Row label="CGST" value={`₹${Number(invoice.total_cgst || 0).toFixed(2)}`} />
            <Row label="SGST" value={`₹${Number(invoice.total_sgst || 0).toFixed(2)}`} />
            <Row label="Discount" value={`- ₹${Number(invoice.discount || 0).toLocaleString()}`} />
            <div className="flex justify-between font-bold text-base border-t pt-2">
              <span>Total</span><span>₹{Number(invoice.total_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-green-700">
              <span>Paid</span><span>₹{Number(invoice.paid_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-red-700 font-semibold">
              <span>Balance</span><span>₹{balance.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t text-sm text-gray-600">
          <span className="font-semibold text-gray-700">Amount in Words: </span>
          {numberToWordsIndian(invoice.total_amount)} Only
        </div>

        {/* BANK DETAILS + TERMS — side by side, matches standard tax-invoice layout */}
        {(company?.bank_name || company?.bank_account_no || company?.terms_conditions) && (
          <div className="mt-6 grid md:grid-cols-2 gap-6 border-t pt-4 text-sm">
            {(company?.bank_name || company?.bank_account_no) && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Bank Details</h4>
                {company?.bank_name       && <p className="text-gray-500">Name: <span className="text-gray-700">{company.bank_name}</span></p>}
                {company?.bank_account_no && <p className="text-gray-500">Account No: <span className="text-gray-700">{company.bank_account_no}</span></p>}
                {company?.bank_ifsc       && <p className="text-gray-500">IFSC: <span className="text-gray-700">{company.bank_ifsc}</span></p>}
                {company?.bank_branch     && <p className="text-gray-500">Branch: <span className="text-gray-700">{company.bank_branch}</span></p>}
              </div>
            )}
            {company?.terms_conditions && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Terms and Conditions</h4>
                <p className="text-gray-500 whitespace-pre-line text-xs leading-relaxed">{company.terms_conditions}</p>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="mt-8 border-t pt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Notes:</h4>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* SIGNATURE BLOCK */}
        <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
          <div className="pt-10 border-t border-gray-300 text-center text-gray-500">
            Authorised Signatory For<br /><span className="font-medium text-gray-700">{company?.company_name || "Company"}</span>
          </div>
          <div className="pt-10 border-t border-gray-300 text-center text-gray-500">
            Receiver's Signature
          </div>
        </div>

      
      </div>

      {/* PAYMENT HISTORY */}
      {payments.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6 print:hidden">
          <h3 className="font-bold text-gray-800 mb-4">💳 Payment History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-3 font-semibold text-gray-500">#</th>
                  <th className="p-3 font-semibold text-gray-500">Date</th>
                  <th className="p-3 font-semibold text-gray-500">Amount</th>
                  <th className="p-3 font-semibold text-gray-500">Mode</th>
                  <th className="p-3 font-semibold text-gray-500">Recorded By</th>
                  <th className="p-3 font-semibold text-gray-500">Note</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="p-3 text-gray-400">{i + 1}</td>
                    <td className="p-3">{fmtDate(p.created_at || p.payment_date)}</td>
                    <td className="p-3 font-semibold text-green-700">₹{Number(p.amount || 0).toLocaleString()}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">{p.mode}</span>
                    </td>
                    <td className="p-3 text-gray-600">{p.recorded_by_name || "-"}</td>
                    <td className="p-3 text-gray-400 italic">{p.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RECORD PAYMENT MODAL */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-bold text-gray-800 text-lg">💰 Record Payment</h3>
              <button onClick={() => setShowPayModal(false)} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>

            {/* Balance info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-amber-700">
                Total: <strong>₹{Number(invoice.total_amount || 0).toLocaleString()}</strong> &nbsp;|&nbsp;
                Paid: <strong className="text-green-700">₹{Number(invoice.paid_amount || 0).toLocaleString()}</strong> &nbsp;|&nbsp;
                Balance: <strong className="text-red-700">₹{balance.toLocaleString()}</strong>
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Amount Received *</label>
                <input
                  type="number" min="1" max={balance}
                  placeholder={`Max ₹${balance.toLocaleString()}`}
                  value={payForm.amount}
                  onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-300"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Mode</label>
                <select value={payForm.mode} onChange={e => setPayForm(p => ({ ...p, mode: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-amber-300">
                  {["Cash","UPI","Cheque","Bank Transfer","NEFT","RTGS"].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Date</label>
                <input type="date" value={payForm.date}
                  onChange={e => setPayForm(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Note (optional)</label>
                <input placeholder="Cheque no., UTR, reference..." value={payForm.note}
                  onChange={e => setPayForm(p => ({ ...p, note: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowPayModal(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={handleRecordPayment} disabled={payLoading || !payForm.amount}
                className="flex-1 py-3 rounded-xl bg-amber-500 text-white font-semibold text-sm disabled:opacity-40">
                {payLoading ? "Saving..." : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Indian numbering (Lakh/Crore) amount-in-words — purely a display helper,
// computed live from total_amount, never stored (would drift if the
// invoice is ever edited).
function numberToWordsIndian(num) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const twoDigits = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  };
  const threeDigits = (n) => {
    if (n < 100) return twoDigits(n);
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigits(n % 100) : "");
  };

  let n = Math.round(Number(num) || 0);
  if (n === 0) return "Zero Rupees";

  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh  = Math.floor(n / 100000);    n %= 100000;
  const thousand = Math.floor(n / 1000);   n %= 1000;
  const hundred = n;

  let parts = [];
  if (crore)    parts.push(threeDigits(crore) + " Crore");
  if (lakh)     parts.push(threeDigits(lakh) + " Lakh");
  if (thousand) parts.push(threeDigits(thousand) + " Thousand");
  if (hundred)  parts.push(threeDigits(hundred));

  return parts.join(" ") + " Rupees";
}